import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import styles from './BookDetail.module.css';
import { HeartIcon, LoaderIcon, CheckIcon, ArrowLeftIcon } from '../components/Icons';

const BookDetail = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPathRef = useRef(location.state?.from || '/discover');
  const [book, setBook] = useState(null);
  const [volumes, setVolumes] = useState([]);
  const [activeVolumeId, setActiveVolumeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [price, setPrice] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [favoriteBookId, setFavoriteBookId] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [completedChapterIds, setCompletedChapterIds] = useState(new Set());

  useEffect(() => {
    if (bookId) {
      fetchBookDetail();
      fetchBookVolumes();
      checkFavoriteStatus();
      fetchCompletedChapters();
    }
  }, [bookId]);

  const fetchBookDetail = async () => {
    try {
      const response = await api.get(`/novels/${bookId}`);
      setBook(response.data);
    } catch (error) {
      console.error('获取书籍详情失败:', error);
      setError('获取书籍详情失败');
    }
  };

  const fetchBookVolumes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/volumes/?novel_id=${bookId}`);
      const volumesData = response.data;
      setVolumes(volumesData);
      
      // 设置第一个卷为默认激活状态
      if (volumesData.length > 0) {
        setActiveVolumeId(volumesData[0].id);
      }
    } catch (error) {
      console.error('获取卷列表失败:', error);
      setError('获取卷列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedChapters = async () => {
    try {
      const response = await api.get('/completed-chapters/');
      const entries = Array.isArray(response.data) ? response.data : [];
      const targetIds = entries
        .filter((entry) => entry.novel_id === Number(bookId))
        .map((entry) => entry.chapter_id);
      setCompletedChapterIds(new Set(targetIds));
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error('获取完成章节状态失败:', error);
      }
      setCompletedChapterIds(new Set());
    }
  };

  const handleBack = () => {
    const target = fromPathRef.current || '/discover';
    if (location.state?.historyReturn) {
      const historyFrom = location.state?.from || target;
      navigate(target, {
        state: {
          historyReturn: location.state.historyReturn,
          from: historyFrom,
        },
      });
      return;
    }
    navigate(target);
  };

  const checkFavoriteStatus = async () => {
    try {
      const response = await api.get(`/favorite-books/check/${bookId}`);
      if (response.data?.is_favorited) {
        setFavoriteBookId(response.data.favorite_id);
      } else {
        setFavoriteBookId(null);
      }
    } catch (error) {
      console.error('检查收藏状态失败:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!bookId) return;
    setFavoriteLoading(true);
    try {
      if (favoriteBookId) {
        await api.delete(`/favorite-books/${favoriteBookId}`);
        setFavoriteBookId(null);
      } else {
        const response = await api.post('/favorite-books/', { novel_id: Number(bookId) });
        setFavoriteBookId(response.data?.id ?? null);
      }
    } catch (error) {
      console.error('更新收藏状态失败:', error);
      if (error?.response?.status === 401) {
        alert('请先登录后再收藏');
      } else {
        alert('收藏操作失败，请稍后重试');
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleChapterClick = async (chapter) => {
    console.log('点击章节:', chapter.title);

    try {
      await api.post('/history-chapters/', {
        novel_id: Number(bookId),
        chapter_id: chapter.id,
      });
    } catch (error) {
      console.error('记录阅读历史失败:', error);
    }

    navigate(`/read/${bookId}/${chapter.id}`, {
      state: {
        from: `/book/${bookId}`,
        historyReturn: location.state?.historyReturn || location.state?.from || null,
      },
    });
  };

  const getActiveVolume = () => {
    return volumes.find(volume => volume.id === activeVolumeId);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <ArrowLeftIcon size={20} />
        </button>
      </div>
    );
  }

  if (!book) {
    return (
      <div className={styles.error}>
        <p>书籍不存在</p>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <ArrowLeftIcon size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.bookDetail}>
      {/* 返回按钮 */}
      <div className={styles.header}>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <ArrowLeftIcon size={20} />
        </button>
      </div>

      {/* 书籍基本信息 */}
      <div className={styles.bookInfo}>
    
        
        <div className={styles.bookMeta}>
          <div className={styles.bookTitleRow}>
            <h1 className={styles.bookTitle}>{book.title}</h1>
            <button
              type="button"
              className={`${styles.favoriteBtn} ${favoriteBookId ? styles.favoriteBtnActive : ''}`}
              onClick={handleFavoriteToggle}
              disabled={favoriteLoading}
              title={favoriteBookId ? '取消收藏' : '收藏此书'}
              aria-label={favoriteBookId ? '取消收藏' : '收藏此书'}
            >
              {favoriteLoading ? (
                <LoaderIcon size={16} />
              ) : (
                <HeartIcon size={22} filled={!!favoriteBookId} />
              )}
            </button>
          </div>
          <p className={styles.bookAuthor}>作者：{book.author}</p>
          
          <div className={styles.bookStats}>
            <span className={styles.statItem}>
              状态：{book.is_completed ? '已完结' : '连载中'}
            </span>
            <span className={styles.statItem}>
              卷数：{volumes.length}
            </span>
            <span className={styles.statItem}>
              总章节：{volumes.reduce((sum, vol) => sum + (vol.chapters?.length || 0), 0)}
            </span>
          </div>

          {/* {book.uploader_name && (
            <p className={styles.uploader}>上传人：{book.uploader_name}</p>
          )} */}

          {/* {book.categories && book.categories.length > 0 && (
            <div className={styles.categories}>
              <span className={styles.categoriesLabel}>分类：</span>
              {book.categories.map(category => (
                <span key={category.id} className={styles.categoryTag}>
                  {category.name}
                </span>
              ))}
            </div>
          )} */}
        </div>
      </div>

      {/* 书籍简介 */}
      {book.description && (
        <div className={styles.description}>
          {/* <h3 className={styles.sectionTitle}>简介</h3> */}
          <div
            className={`${styles.descriptionContent} ${
              showFullDescription ? styles.descriptionExpandedContent : styles.descriptionCollapsedContent
            }`}
          >
            <p
              className={`${styles.descriptionText} ${
                showFullDescription ? styles.descriptionExpanded : styles.descriptionCollapsed
              }`}
            >
              {book.description}
            </p>
          </div>
          <button
            type="button"
            className={`${styles.descriptionToggle} ${
              showFullDescription ? styles.toggleExpanded : styles.toggleCollapsed
            }`}
            onClick={() => setShowFullDescription(prev => !prev)}
          >
            {showFullDescription ? '收起简介' : '展开更多'}
          </button>
        </div>
      )}

      {/* 卷和章节列表 */}
      <div className={styles.volumesSection}>
        <h3 className={styles.sectionTitle}>章节目录</h3>
        
        {volumes.length === 0 ? (
          <div className={styles.noVolumes}>
            <p>暂无章节内容</p>
          </div>
        ) : (
          <>
            {/* 卷标签 */}
            <div className={styles.volumeTabs}>
              {volumes.map(volume => (
                <button
                  key={volume.id}
                  className={`${styles.volumeTab} ${
                    activeVolumeId === volume.id ? styles.active : ''
                  }`}
                  onClick={() => setActiveVolumeId(volume.id)}
                >
                  {volume.title}
                  <span className={styles.chapterCount}>
                    ({volume.chapters?.length || 0}章)
                  </span>
                </button>
              ))}
            </div>

            {/* 章节列表 */}
            <div className={styles.chapterList}>
              {getActiveVolume()?.chapters?.length > 0 ? (
                getActiveVolume().chapters
                  .sort((a, b) => a.order_index - b.order_index)
                  .map(chapter => (
                    <div
                      key={chapter.id}
                      className={styles.chapterItem}
                      onClick={() => handleChapterClick(chapter)}
                    >
                      <span className={styles.chapterTitle}>{chapter.title}</span>
                      <div className={styles.chapterItemMeta}>
                        <span className={styles.chapterIndex}>第{chapter.order_index}章</span>
                        {completedChapterIds.has(chapter.id) && (
                          <span className={styles.chapterStatus}>
                            <CheckIcon size={14} />
                            已完成
                          </span>
                        )}
                      </div>
                    </div>
                  ))
              ) : (
                <div className={styles.noChapters}>
                  <p>该卷暂无章节</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookDetail;
